/**
 * Hook for managing push notifications in the mobile app
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/features/auth/hooks/useSession';
import {
  isNative,
  getPlatform,
  registerPushNotifications,
  getPushToken,
  onPushAction,
} from '@/lib/capacitor';
import { apiFetch } from '@/lib/apiClient';
import { buildConversationPath, resolveWorkspaceId } from '@/lib/workspace-routing';
import { setPendingAgentReport } from '@/lib/agent-report';
import { setPendingDeepLink } from '@/lib/pending-deep-link';

export function usePushNotifications() {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const navigate = useNavigate();
  const registeredRef = useRef(false);

  useEffect(() => {
    if (!isNative() || !token || registeredRef.current) return;

    const setupPush = async () => {
      try {
        // Register for push notifications — returns the device token directly
        const pushToken = await registerPushNotifications();

        if (!pushToken) {
          console.log('[push] No token available after registration');
          return;
        }

        // Send token to backend
        const platform = getPlatform();
        await apiFetch('/auth/push-token', {
          method: 'POST',
          authToken: token,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            deviceToken: pushToken,
            platform: platform,
            deviceInfo: {
              platform,
              version: navigator.userAgent,
            },
          }),
        });

        console.log('[push] Token registered with backend');
        registeredRef.current = true;
      } catch (error) {
        console.error('[push] Failed to setup push notifications:', error);
      }
    };

    setupPush();
  }, [token]);

  // Handle push notification taps
  useEffect(() => {
    if (!isNative()) return;

    const cleanup = onPushAction((action) => {
      const data = action.notification.data;
      const conversationId = data?.conversationId;
      const targetWorkspaceId = data?.workspaceId || workspaceId;

      // Navigate based on the notification type
      if (data?.deepLink) {
        const deepLink = data.deepLink as string;
        if (deepLink.startsWith('xavior://agent-report/')) {
          const runId = deepLink.slice('xavior://agent-report/'.length);
          setPendingAgentReport(runId);
          navigate('/');
        } else if (deepLink.startsWith('xavior://')) {
          // Try structured deep link (inbox, calendar, tasks, conversation, etc.)
          if (setPendingDeepLink(deepLink)) {
            navigate('/');
          } else {
            // Unknown deep link — try direct path navigation as fallback
            const path = deepLink.replace('xavior://', '/');
            navigate(path);
          }
        }
      } else if (conversationId && data?.sessionId) {
        // Navigate to chat session
        navigate(buildConversationPath(targetWorkspaceId, conversationId, { view: 'chat' }));
      } else if (conversationId) {
        // Navigate to conversation
        navigate(buildConversationPath(targetWorkspaceId, conversationId));
      } else if (data?.objectId) {
        // Navigate to object
        navigate(`/object/${data.objectId}`);
      }
    });

    return cleanup;
  }, [navigate, workspaceId]);
}
